import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckoutApiError, CheckoutStateError } from '../api/api';
import useLatestRef from '../../../hooks/useLatestRef';
function notifyHost(callback) {
    try {
        callback();
    }
    catch (failure) {
        console.error('One Page Checkout host callback failed', failure);
    }
}
export default function useCheckoutSession(options) {
    const host = useLatestRef(options);
    const [open, setOpen] = useState(false);
    const [snapshot, setSnapshot] = useState(null);
    const [success, setSuccess] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);
    const action = useRef(null);
    const [error, setError] = useState('');
    const [pickupError, setPickupError] = useState('');
    const [startVariant, setStartVariant] = useState('');
    const [policyAgreement, setPolicyAgreement] = useState(false);
    const [codeOpen, setCodeOpen] = useState(false);
    const [code, setCode] = useState('');
    const trackedOrder = useRef(null);
    const sessionToken = useRef(null);
    const sessionApi = useRef(null);
    const operationId = useRef(0);
    const mounted = useRef(true);
    const authenticationHandoff = useRef(false);
    const sessionDiscarded = useRef(false);
    const currentSnapshot = useRef(null);
    const setAction = useCallback((next) => {
        action.current = next;
        setPendingAction(next);
    }, []);
    const authenticate = useCallback(async (destination) => {
        const { current } = currentSnapshot;
        if (!current || action.current?.type === 'authenticate' || action.current?.type === 'submit')
            return;
        operationId.current += 1;
        const currentOperation = operationId.current;
        authenticationHandoff.current = true;
        setAction({ type: 'authenticate', destination });
        setError('');
        try {
            const result = await (sessionApi.current ?? host.current.api).authenticate(current, destination);
            if (mounted.current && currentOperation === operationId.current)
                host.current.navigate(result.redirect);
        }
        catch (failure) {
            if (mounted.current && currentOperation === operationId.current) {
                authenticationHandoff.current = false;
                setError(failure instanceof Error ? failure.message : host.current.translate('one_page_checkout.error'));
                setAction(null);
            }
        }
    }, [host, setAction]);
    const update = useCallback((next) => {
        const previous = currentSnapshot.current;
        if (previous && previous.code !== next.code)
            setPolicyAgreement(false);
        currentSnapshot.current = next;
        sessionToken.current = next.token;
        notifyHost(() => host.current.onCartQuantityChange(next.cartQuantity));
        if (next.stage === 'account') {
            setSnapshot(null);
            // A submit may discover a newly incomplete account. The request has completed.
            if (action.current?.type === 'submit')
                setAction(null);
            authenticate('login');
            return;
        }
        setSnapshot(next);
        setCode(current => (next.codeErrors.length ? current : next.code || ''));
        if (next.codeErrors.length)
            setCodeOpen(true);
    }, [authenticate, host, setAction]);
    const run = useCallback(async (nextAction, task, onSuccess, onFailure, onStale) => {
        operationId.current += 1;
        const currentOperation = operationId.current;
        setAction(nextAction);
        setError('');
        try {
            const result = await task();
            if (mounted.current && currentOperation === operationId.current)
                onSuccess(result);
            else
                onStale?.(result);
        }
        catch (failure) {
            if (mounted.current && currentOperation === operationId.current) {
                if (failure instanceof CheckoutStateError) {
                    const totalChanged = currentSnapshot.current?.total !== failure.snapshot.total;
                    update(failure.snapshot);
                    const hasFieldError = Boolean(failure.snapshot.selectionError) || failure.snapshot.codeErrors.length > 0;
                    let message = '';
                    if (totalChanged)
                        message = host.current.translate('one_page_checkout.price_changed');
                    else if (!hasFieldError)
                        message = host.current.translate('one_page_checkout.error');
                    setError(message);
                }
                else {
                    const message = failure instanceof Error ? failure.message : host.current.translate('one_page_checkout.error');
                    if (onFailure)
                        onFailure(message, failure);
                    else
                        setError(message);
                }
            }
        }
        finally {
            if (mounted.current && currentOperation === operationId.current)
                setAction(null);
        }
    }, [host, setAction, update]);
    const start = useCallback((variantCode, automatic = false) => {
        if (action.current?.type === 'submit')
            return;
        const { api } = host.current;
        if (!automatic)
            setOpen(true);
        authenticationHandoff.current = false;
        sessionDiscarded.current = false;
        currentSnapshot.current = null;
        setStartVariant(variantCode);
        setSnapshot(null);
        setSuccess(null);
        setPickupError('');
        setPolicyAgreement(false);
        setCodeOpen(false);
        setCode('');
        run({ type: 'start' }, () => api.start(variantCode, automatic), next => {
            sessionApi.current = api;
            update(next);
            if (automatic && next.stage !== 'account')
                setOpen(true);
        }, (message, failure) => {
            setError(message);
            // Ordinary PDP visits also probe for a login return; keep those expected rejections quiet.
            if (automatic &&
                failure instanceof CheckoutApiError &&
                failure.code !== 'session_expired' &&
                failure.code !== 'login_required') {
                setOpen(true);
            }
        }, stale => {
            api.close(stale.token).catch(() => undefined);
        });
    }, [host, run, update]);
    const close = useCallback(() => {
        if (action.current?.type === 'submit')
            return false;
        operationId.current += 1;
        const token = sessionToken.current;
        const api = sessionApi.current ?? host.current.api;
        sessionToken.current = null;
        sessionApi.current = null;
        sessionDiscarded.current = false;
        currentSnapshot.current = null;
        setOpen(false);
        setSnapshot(null);
        setSuccess(null);
        setAction(null);
        setPickupError('');
        setError('');
        if (token)
            api.close(token).catch(() => undefined);
        return true;
    }, [host, setAction]);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            // Strict Mode immediately sets this effect up again. Only a real unmount discards the session.
            queueMicrotask(() => {
                if (mounted.current)
                    return;
                operationId.current += 1;
                const token = sessionToken.current;
                sessionToken.current = null;
                if (token && action.current?.type !== 'submit' && !authenticationHandoff.current) {
                    (sessionApi.current ?? host.current.api).closeOnPageHide(token);
                }
            });
        };
    }, [host]);
    useEffect(() => {
        if (!open || !snapshot?.token || success)
            return undefined;
        const { token } = snapshot;
        const discard = () => {
            if (action.current?.type === 'submit' || authenticationHandoff.current)
                return;
            sessionDiscarded.current = true;
            operationId.current += 1;
            (sessionApi.current ?? host.current.api).closeOnPageHide(token);
        };
        window.addEventListener('pagehide', discard);
        return () => window.removeEventListener('pagehide', discard);
    }, [open, snapshot?.token, success, host]);
    useEffect(() => {
        const restore = (event) => {
            if (!event.persisted || !sessionDiscarded.current)
                return;
            // The browser restored React state, but pagehide already discarded the server session.
            operationId.current += 1;
            sessionDiscarded.current = false;
            sessionToken.current = null;
            sessionApi.current = null;
            authenticationHandoff.current = false;
            currentSnapshot.current = null;
            setSnapshot(null);
            setSuccess(null);
            setAction(null);
            setPickupError('');
            setPolicyAgreement(false);
            setCodeOpen(false);
            setCode('');
            setError(host.current.translate('one_page_checkout.session_expired'));
        };
        window.addEventListener('pageshow', restore);
        return () => window.removeEventListener('pageshow', restore);
    }, [host, setAction]);
    const changePickup = (method, id) => {
        if (action.current || !snapshot)
            return;
        setPickupError('');
        if (id === null) {
            setError('');
            // Keep the confirmed server revision and quote until a location is selected.
            setSnapshot({ ...snapshot, selection: { method, id }, selectionError: null });
            return;
        }
        if (method === 'reseller') {
            // A failed direct selection must not restore the previous pickup as ready to submit.
            setSnapshot({ ...snapshot, selection: { method, id: null }, selectionError: null });
        }
        run({
            type: 'pickup',
            selection: { method, id },
        }, () => (sessionApi.current ?? host.current.api).pickup(snapshot, method, id), update, setPickupError);
    };
    const applyCode = (value) => {
        if (action.current || !snapshot)
            return;
        const localSelection = snapshot.selection.method !== currentSnapshot.current?.selection.method ||
            snapshot.selection.id !== currentSnapshot.current?.selection.id;
        run({ type: 'code', action: value.trim() ? 'apply' : 'remove' }, () => (sessionApi.current ?? host.current.api).code(snapshot, value), next => {
            update(next);
            if (localSelection && next.stage === 'delivery') {
                const { method } = snapshot.selection;
                const available = method && next.locations[method].length && !(method === 'reseller' && next.availabilityError);
                setSnapshot({ ...next, selection: { method: available ? method : null, id: null }, selectionError: null });
            }
        });
    };
    const changeCode = (value) => {
        if (action.current)
            return;
        setCode(value);
        if (snapshot?.codeErrors.length)
            setSnapshot(current => (current ? { ...current, codeErrors: [] } : null));
    };
    const canSubmit = !pendingAction &&
        policyAgreement &&
        Boolean(snapshot?.selection.id) &&
        snapshot?.stage === 'delivery' &&
        !(snapshot.selection.method === 'reseller' && snapshot.availabilityError) &&
        snapshot.codeErrors.length === 0 &&
        !(code.trim() !== '' && code !== snapshot.code) &&
        !snapshot.selectionError;
    const submit = () => {
        if (!canSubmit || !snapshot || action.current)
            return;
        run({ type: 'submit' }, () => (sessionApi.current ?? host.current.api).submit(snapshot, policyAgreement, snapshot.total, snapshot.deliveryPrice), result => {
            setSuccess(result);
            notifyHost(() => host.current.onCartQuantityChange(result.cartQuantity));
            if (trackedOrder.current !== result.orderId) {
                trackedOrder.current = result.orderId;
                notifyHost(() => host.current.onPurchase(snapshot, result));
            }
        });
    };
    return {
        open,
        snapshot,
        success,
        busy: pendingAction !== null,
        starting: pendingAction?.type === 'start',
        accountRedirectPending: pendingAction?.type === 'authenticate' && !snapshot,
        submitting: pendingAction?.type === 'submit',
        pendingPickup: pendingAction?.type === 'pickup' ? pendingAction.selection : null,
        codeAction: pendingAction?.type === 'code' ? pendingAction.action : null,
        error,
        pickupError,
        startVariant,
        policyAgreement,
        codeOpen,
        code,
        canSubmit,
        start,
        close,
        changePickup,
        applyCode,
        changeCode,
        authenticate,
        submit,
        setCodeOpen,
        setPolicyAgreement,
    };
}
